import { useMemo } from 'react';
import { COMBINED_CATEGORIES_CONFIG, getAllCombinedCategoryIds } from '@/pages/Jar/jar.constants';

interface FacetData {
  key: String;
  count: number;
}

interface UseCombinedCategoriesProps {
  facets?: { categories: FacetData[] };
}

interface CombinedCategoryOption {
  value: string;
  description: string;
  disabled: boolean;
}

export const useCombinedCategories = ({ facets }: UseCombinedCategoriesProps) => {
  const generateCombinedCategoryOptions = useMemo((): CombinedCategoryOption[] => {
    if (!facets) return [];

    const options: CombinedCategoryOption[] = [];

    Object.entries(COMBINED_CATEGORIES_CONFIG).forEach(([combinedId, config]) => {
      options.push({
        value: combinedId,
        description: config.label,
        disabled: false,
      });
    });

    return options;
  }, [facets]);

  const expandCombinedCategories = useMemo(() => {
    return (categories: string[]): string[] => {
      let expandedCategories = [...categories];

      Object.entries(COMBINED_CATEGORIES_CONFIG).forEach(([combinedId, config]) => {
        if (categories.includes(combinedId)) {
          expandedCategories = [
            ...expandedCategories.filter(cat => cat !== combinedId),
            ...config.categories,
          ];
        }
      });

      return expandedCategories;
    };
  }, []);

  const compressToCombinedCategories = useMemo(() => {
    return (categories: string[]): string[] => {
      let compressedCategories = [...categories];
      const processedIndividualCategories = new Set<string>();

      Object.entries(COMBINED_CATEGORIES_CONFIG).forEach(([combinedId, config]) => {
        const hasAllCategories = config.categories.every(cat => categories.includes(cat));

        if (hasAllCategories) {
          config.categories.forEach(cat => {
            processedIndividualCategories.add(cat);
            compressedCategories = compressedCategories.filter(c => c !== cat);
          });
          compressedCategories.push(combinedId);
        }
      });

      return compressedCategories;
    };
  }, []);

  const getCombinedCategoryLabel = useMemo(() => {
    return (combinedId: string): string => {
      return COMBINED_CATEGORIES_CONFIG[combinedId]?.label || combinedId;
    };
  }, []);

  return {
    generateCombinedCategoryOptions,
    expandCombinedCategories,
    compressToCombinedCategories,
    getCombinedCategoryLabel,
    isCombinedCategory: (categoryId: string) => getAllCombinedCategoryIds().includes(categoryId),
  };
};
